import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaDownload, FaFileExcel, FaWhatsapp, FaSms } from 'react-icons/fa';
import { billsAPI } from '../utils/api';
import './BillManagement.css';

function BillManagement() {
  const [bills, setBills] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadBills = async () => {
    try {
      setLoading(true);
      const from = fromDate ? new Date(fromDate).toISOString() : null;
      const to = toDate ? new Date(toDate).toISOString() : null;
      const response = await billsAPI.getAll(from, to);
      setBills(response.data?.bills || []);
    } catch (error) {
      console.error('Error loading bills:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to load bills. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBills();
  }, []);

  const handleExport = async () => {
    try {
      const from = fromDate ? new Date(fromDate).toISOString() : null;
      const to = toDate ? new Date(toDate).toISOString() : null;
      const blob = await billsAPI.exportCsv(from, to);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bills-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting bills:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to export bills. Please try again.',
      });
    }
  };

  const buildBillMessage = (bill) => {
    const lines = [];

    // Header
    if (bill.restaurant_name) lines.push(String(bill.restaurant_name));
    if (bill.gst_number) lines.push(`GST: ${bill.gst_number}`);
    if (bill.gst_rate_percent != null && bill.gst_rate_percent !== '') lines.push(`GST Rate: ${bill.gst_rate_percent}%`);

    lines.push(`Bill No: ${bill.bill_number}`);
    if (bill.customer_name) lines.push(`Customer: ${bill.customer_name}`);
    if (bill.table_number != null) lines.push(`Table: ${bill.table_number}`);
    if (bill.mobile) lines.push(`Phone: ${bill.mobile}`);
    lines.push(`Date: ${new Date(bill.created_at).toLocaleString()}`);

    // Customer bill link (PDF)
    if (bill.share_token) {
      const publicAppBase = (process.env.REACT_APP_PUBLIC_APP_URL || 'https://app.menubazzar.com').replace(/\/+$/, '');
      const publicLink = `${publicAppBase}/bill/${bill.share_token}`;
      lines.push('');
      lines.push(`View PDF: ${publicLink}`);
    }

    lines.push('');
    lines.push('Items:');
    (bill.items || []).forEach((item) => {
      lines.push(
        `- ${item.name} x${item.quantity} @ ₹${item.unitPrice} = ₹${item.lineTotal}`,
      );
    });
    lines.push('');
    lines.push(`Subtotal: ₹${bill.subtotal}`);
    lines.push(`Tax: ₹${bill.tax}`);
    lines.push(`Total: ₹${bill.total}`);
    return lines.join('\n');
  };

  const handleShareWhatsApp = (bill) => {
    const text = encodeURIComponent(buildBillMessage(bill));
    const phone = bill.mobile ? bill.mobile.replace(/[^0-9]/g, '') : '';
    const url = phone
      ? `https://wa.me/${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  const handleShareSms = (bill) => {
    const text = encodeURIComponent(buildBillMessage(bill));
    const phone = bill.mobile ? bill.mobile.replace(/[^0-9]/g, '') : '';
    const url = phone ? `sms:${phone}?body=${text}` : `sms:?body=${text}`;
    window.open(url, '_blank');
  };

  return (
    <div className="bill-management">
      {notification && (
        <div className={`admin-notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button
            type="button"
            className="admin-notification-close"
            onClick={() => setNotification(null)}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      )}

      <header className="management-header">
        <Link to="/dashboard" className="back-btn">
          <FaArrowLeft /> Back
        </Link>
        <h1 className="management-title">Bills</h1>
        <div className="filter-controls">
          <div className="date-filters">
            <label>
              From:{' '}
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </label>
            <label>
              To:{' '}
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>
            <button type="button" className="filter-btn" onClick={loadBills} disabled={loading}>
              <FaDownload /> Apply
            </button>
          </div>
          <button
            type="button"
            className="export-btn"
            onClick={handleExport}
            disabled={loading || bills.length === 0}
          >
            <FaFileExcel /> Download CSV/Excel
          </button>
        </div>
      </header>

      <div className="management-content">
        {loading ? (
          <div className="no-orders">
            <p>Loading bills...</p>
          </div>
        ) : bills.length === 0 ? (
          <div className="no-orders">
            <p>No bills found for the selected period.</p>
          </div>
        ) : (
          <div className="bills-table-wrapper">
            <table className="bills-table">
              <thead>
                <tr>
                  <th>Bill No</th>
                  <th>Date / Time</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Table</th>
                  <th>Subtotal</th>
                  <th>Tax</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((bill) => (
                  <tr key={bill.id}>
                    <td>{bill.bill_number}</td>
                    <td>{new Date(bill.created_at).toLocaleString()}</td>
                    <td>{bill.customer_name || '-'}</td>
                    <td>{bill.mobile || '-'}</td>
                    <td>{bill.table_number != null ? bill.table_number : '-'}</td>
                    <td>₹{bill.subtotal}</td>
                    <td>₹{bill.tax}</td>
                    <td>₹{bill.total}</td>
                    <td>
                      <button
                        type="button"
                        className="icon-btn"
                        title="Share via WhatsApp"
                        onClick={() => handleShareWhatsApp(bill)}
                      >
                        <FaWhatsapp />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="Share via SMS"
                        onClick={() => handleShareSms(bill)}
                      >
                        <FaSms />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default BillManagement;


